import type { FundingOutreachCommunication } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { formatOccurredAt } from "@mixitone/util";
import clsx from "clsx";
import { CommunicationBody } from "components/Pages/Funding/CommunicationBody";
import { FundingOutreachFormController } from "components/Pages/Funding/FundingOutreachFormController";
import { sentimentLabel, sentimentStyles } from "lib/fundingSentiment";

/** Shape of communication for display (model instances have these at runtime). */
type CommView = {
  id: string;
  direction: "inbound" | "outbound";
  channel?: "email" | "manual";
  from_email?: string | null;
  from_name?: string | null;
  to_email?: string | null;
  subject?: string | null;
  body_html?: string | null;
  body_text?: string | null;
  occurred_at?: string | Date | null;
  opened_at?: string | Date | null;
  sent_by_account_user?: { name?: string | null } | null;
};

/** Prefer name over email when present; optionally parse "Name <email>" format. */
function displayLabel(email: string | undefined | null, name?: string | null): string {
  const n = typeof name === "string" ? name.trim() : "";
  if (n) return n;
  const e = email ?? "";
  const match = e.match(/^(.+?)\s*<[^>]+>$/);
  if (match) {
    const part = match[1].trim();
    if (part && !part.includes("@")) return part;
  }
  return e;
}

const MessageStackMessageContent = ({ comm }: { comm: FundingOutreachCommunication }) => {
  const controller = FundingOutreachFormController.use();
  const c = comm as unknown as CommView;
  const isOutbound = c.direction === "outbound";
  const analysis = !isOutbound ? controller.state.analysesByCommId?.[c.id] : null;
  const sentiment = analysis?.sentiment ?? "unknown";
  const styles = !isOutbound ? sentimentStyles(sentiment) : null;
  const when = formatOccurredAt(c.occurred_at ?? undefined);

  return (
    <li className="flex w-full flex-col gap-0.5">
      <div
        className={clsx(
          "max-w-[92%] text-[11px] leading-tight text-gray-500 tabular-nums",
          isOutbound ? "self-end text-right" : "self-start text-left",
        )}
      >
        <span className="sr-only">{isOutbound ? "Sent " : "Received "}</span>
        {when}
      </div>
      <div className={clsx("flex w-full", isOutbound ? "justify-end" : "justify-start")}>
        <button
          type="button"
          onClick={() => controller.actionSetViewingCommunication(comm)}
          className={clsx(
            "max-w-[92%] cursor-pointer overflow-hidden rounded-md border border-gray-200",
            "bg-white px-2 py-1.5 text-left text-sm transition-shadow",
            "focus:outline-none focus:ring-2 focus:ring-gray-400",
            "shadow-sm hover:shadow",
            {
              "border-r-[3px] border-r-brand-600 bg-brand-50/50": isOutbound,
              [`border-l-[3px] bg-gray-50/80 ${styles?.border ?? "border-l-gray-400"}`]: !isOutbound,
            },
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-[11px] font-medium leading-tight text-gray-900">
              {isOutbound
                ? c.sent_by_account_user?.name
                  ? `You (${c.sent_by_account_user.name}) → ${displayLabel(c.to_email)}`
                  : `You → ${displayLabel(c.to_email)}`
                : c.channel === "manual"
                  ? `${c.from_name ?? "Note"} → You`
                  : `${displayLabel(c.from_email, c.from_name)} → You`}
            </span>
            {isOutbound ? (
              <span
                className="inline-flex shrink-0 items-center gap-1"
                title={
                  c.opened_at
                    ? `Opened ${formatOccurredAt(c.opened_at)}`
                    : "Recipient has not opened this email yet"
                }
              >
                {c.opened_at ? (
                  <>
                    <span className="sr-only">Opened {formatOccurredAt(c.opened_at)}</span>
                    <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                    <span className="text-[10px] font-medium leading-none text-emerald-800">Viewed</span>
                  </>
                ) : (
                  <>
                    <span className="sr-only">Not opened</span>
                    <span
                      className="box-border h-2.5 w-2.5 shrink-0 rounded-full border-2 border-gray-400 bg-transparent"
                      aria-hidden
                    />
                  </>
                )}
              </span>
            ) : (
              analysis && (
                <span
                  className={`inline-flex shrink-0 rounded-full border px-1 py-px text-[9px] font-medium leading-none ${styles?.badge ?? ""}`}
                >
                  {sentimentLabel(sentiment)}
                </span>
              )
            )}
          </div>
          {c.subject && (
            <div className="mt-0.5 truncate text-[11px] font-medium leading-tight text-gray-700">
              {c.subject}
            </div>
          )}
          <CommunicationBody
            bodyHtml={c.body_html}
            bodyText={c.body_text}
            className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-gray-600 [&_a]:text-blue-600 [&_p]:my-0"
          />
          <div className="mt-0.5 text-[10px] leading-tight text-gray-500 underline decoration-gray-400 underline-offset-1">
            View full message
          </div>
        </button>
      </div>
    </li>
  );
};

export const MessageStackMessage = ApplicationView(MessageStackMessageContent);
